import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, makeStyles, Radio, RadioGroup, TextField } from '@material-ui/core';
import tinycolor from 'tinycolor2';

import { FIELD_TYPES , Schema as SchemaType, SchemaProperty, uiSchema as uiSchemaType, UISchemaProperty, FormSettings } from './types';
import { joinClassNames } from '../../util/util';
import ControlButton from '../Controls/ControlButton';
import { useConfiguration } from '../../contexts/ConfigurationContext';

const formStyles = makeStyles((_theme) => ({
	formField: {
		fontSize: '0.875em',
		width: '100%',
		'&:not(:last-child)': {
			marginBottom: '10px',
		},
	},
	choicesField: {
		width: '100%',
		backgroundColor: '#ffffff',
		borderRadius: "4px",
		boxShadow: '0 1px 3px rgba(31, 26, 23, 0.08)',
		padding: "10px",
		'&:not(:last-child)': {
			marginBottom: '10px',
		},
	},
	formLabelBold: {
		marginBottom: '10px',
		color: '#000000',
		fontSize: '0.75em',
		fontWeight: 500,
	},
	formLabelBoldMobile: {
		marginBottom: '10px',
		color: '#000000',
		fontSize: '1em',
		fontWeight: 500,
	},
	formLabel: {
		marginBottom: '4px',
	},
	formInvalid: {
		'& fieldset': {
			border: '1px solid #f44352',
		}
	},
	formChoicesInvalid: {
		border: '1px solid #f44352',
	},
}));

interface DynamicFormProps {
	schema: SchemaType;
	uiSchema: uiSchemaType;
	className?: string;
	primaryColor?: string;
	styles?: { btnColor: string, btnBackground: string };
	settings?: FormSettings;
	isMobile?: boolean;
	formData: Record<string, string>;
	validations: Record<string, string>;
	onFieldChange?: (_fieldId: string, _value: any) => void;
	onSubmit: () => void;
	hoverStyle?: React.CSSProperties;
	containerRef: React.RefObject<HTMLDivElement>;
};

interface FieldRowProps {
	fieldId: string;
	schemaProps: SchemaProperty;
	uiSchemaProps: UISchemaProperty;
	value: string;
	validationMsg: string;
	isMobile?: boolean;
	primaryColor?: string;
	textColor?: string;
	formFieldClassName: string;
	choicesFieldClassName: string;
	formLabelBoldClassName: string;
	formLabelBoldMobileClassName: string;
	formInvalidClassName: string;
	formChoicesInvalidClassName: string;
	allFieldsRequired?: boolean;
	onFieldChange?: (_fieldId: string, _value: any) => void;
}

const FieldRow = React.memo(({
	fieldId,
	schemaProps,
	uiSchemaProps,
	value,
	validationMsg,
	isMobile,
	primaryColor,
	textColor,
	formFieldClassName,
	choicesFieldClassName,
	formLabelBoldClassName,
	formLabelBoldMobileClassName,
	formInvalidClassName,
	formChoicesInvalidClassName,
	allFieldsRequired,
	onFieldChange
}: FieldRowProps) => {
	const handleTextFieldChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		onFieldChange && onFieldChange(fieldId, event.target.value);
	}, [fieldId, onFieldChange]);

	const handleMultipleChoiceFieldChange = useCallback((option: string) => {
		const previousValue = value ? value.split(', ') : [];
		const nextValue = previousValue.includes(option)
			? previousValue.filter((item) => item !== option)
			: [...previousValue, option];

		onFieldChange && onFieldChange(fieldId, nextValue.join(', '));
	}, [fieldId, onFieldChange, value]);

	const handleSingleChoiceFieldChange = useCallback((option: string) => {
		onFieldChange && onFieldChange(fieldId, option);
	}, [fieldId, onFieldChange]);

	const labelClassName = isMobile ? formLabelBoldMobileClassName : formLabelBoldClassName;
	const isMultiline = uiSchemaProps.type === FIELD_TYPES.Multiline;
	const isRequired = uiSchemaProps.isRequired;
	const placeholder = uiSchemaProps.placeholder ? `${schemaProps.title}${!allFieldsRequired && isRequired ? ' *' : ''}` : undefined;
	const choiceValues = value ? value.split(', ') : [];

	switch (uiSchemaProps.type) {
		case FIELD_TYPES.Input:
		case FIELD_TYPES.Multiline:
			return <>
				<TextField
					id={fieldId}
					required={isRequired}
					value={value}
					placeholder={placeholder}
					multiline={isMultiline}
					minRows={3}
					maxRows={10}
					onChange={handleTextFieldChange}
					variant="outlined"
					InputProps={{
						style: {
							backgroundColor: "#ffffff",
							color: textColor,
							fontSize: "inherit",
							fontWeight: 400
						},
					}}
					InputLabelProps={{
						style: {
							fontSize: "inherit",
							fontWeight: 500,
							color: textColor,
						},
					}}
					className={joinClassNames(validationMsg ? formInvalidClassName : '', formFieldClassName)}
				/>
				{validationMsg && <div className='qplayer-register-form__error-message'>{validationMsg}</div>}
			</>;
		case FIELD_TYPES.MultipleChoice:
			return <>
				<FormControl component="fieldset" className={joinClassNames(validationMsg ? formChoicesInvalidClassName : '', choicesFieldClassName)}>
					<FormLabel component="div" className={labelClassName} style={{ color: textColor }}>
						{uiSchemaProps.label ? schemaProps.title : undefined}
						{!allFieldsRequired && isRequired && <span> *</span>}
					</FormLabel>
					<FormGroup>
						{(uiSchemaProps.options || []).map((option) => (
							<FormControlLabel
								key={option}
								control={<Checkbox
									name={option}
									checked={choiceValues.includes(option)}
									style={{ color: primaryColor }}
									onChange={() => handleMultipleChoiceFieldChange(option)}/>}
								label={option}
							/>
						))}
					</FormGroup>
				</FormControl>

				{validationMsg && <div className='qplayer-register-form__error-message'>{validationMsg}</div>}
			</>;
		case FIELD_TYPES.SingleChoice:
			return <>
				<FormControl component="fieldset" className={joinClassNames(validationMsg ? formChoicesInvalidClassName : '', choicesFieldClassName)}>
					<FormLabel component="div" className={labelClassName} style={{ color: textColor }}>
						{uiSchemaProps.label ? schemaProps.title : undefined}
						{!allFieldsRequired && isRequired && <span> *</span>}
					</FormLabel>
					<RadioGroup value={value}>
						{(uiSchemaProps.options || []).map((option) => (
							<FormControlLabel
								key={option}
								control={<Radio name={option} style={{ color: primaryColor }} onChange={() => handleSingleChoiceFieldChange(option)} />}
								label={option}
								value={option}
							/>
						))}
					</RadioGroup>
				</FormControl>

				{validationMsg && <div className='qplayer-register-form__error-message'>{validationMsg}</div>}
			</>;
		default:
			return null;
	}
});

const DynamicForm: React.FC<DynamicFormProps> = ({ containerRef, schema, uiSchema, primaryColor, isMobile, validations, formData, hoverStyle, settings, onFieldChange, onSubmit }) => {
    const classes = formStyles();
	const { t: translator } = useTranslation();

	const configParams = useConfiguration().configParams;

	const [isPolicyAgreed, setIsPolicyAgreed] = useState(false);

	const policyUrl = configParams?.formPolicyUrl || settings?.privacyPolicyLink || '';

	const handlePolicyAgreedChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setIsPolicyAgreed(event.target.checked);
	}, []);

	const allFieldsRequired = useMemo(() => {
		return Object.values(uiSchema.properties).every((prop) => prop.isRequired);
	}, [uiSchema.properties]);

	// order the fields by the order propery and extract the key for each field in the format { id: key }
	const orderedFields = useMemo(() => {
		return Object.keys(uiSchema.properties)
			.map((key) => ({ id: key, order: uiSchema.properties[key].order }))
			.sort((a, b) => a.order - b.order);
	}, [uiSchema.properties]);

	return <div
		ref={containerRef}
		className={joinClassNames('qplayer-register-form__dynamic-form')}
		style={{ backgroundColor: tinycolor.mix(primaryColor ?? '#126cfc', '#ffffff', 97.5).toString() }}
	>
		<div className='qplayer-register__description' style={{ marginTop: "0", paddingBottom: "8px" }}>
			<span className={configParams.isBonnierDi ? "qplayer-bold-font" : "qplayer-register__text--bold"}>{translator("REGISTRATION_PAGE_FORM_DES_BOLD")}</span> {translator("REGISTRATION_PAGE_FORM_DES")}{allFieldsRequired && <span> {translator('REGISTRATION_PAGE_ALL_FIELDS_REQUIRED')}</span>}
		</div>

		<div
			className='qplayer-register-form__field-container'
			style={{ '--qplayer-register-scrollbar-color': primaryColor ?? '#126cfc' } as React.CSSProperties}
		>
			{orderedFields.map((item) => (
				<FieldRow
					key={item.id}
					fieldId={item.id}
					schemaProps={schema.properties[item.id]}
					uiSchemaProps={uiSchema.properties[item.id]}
					value={formData[item.id] || ''}
					validationMsg={validations[item.id] || ''}
					isMobile={isMobile}
					primaryColor={primaryColor}
					formFieldClassName={classes.formField}
					choicesFieldClassName={classes.choicesField}
					formLabelBoldClassName={classes.formLabelBold}
					formLabelBoldMobileClassName={classes.formLabelBoldMobile}
					formInvalidClassName={classes.formInvalid}
					formChoicesInvalidClassName={classes.formChoicesInvalid}
					onFieldChange={onFieldChange}
					allFieldsRequired={allFieldsRequired}
				/>
			))}
		</div>

		<div className="qplayer-register-form__disclaimer">
			<Checkbox
				checked={isPolicyAgreed}
				onChange={handlePolicyAgreedChange}
				style={{ color: primaryColor, padding: 0, marginRight: '8px' }}
			/>
			<span>
				{`${translator("REGISTRATION_PAGE_FORM_POLICY_PRE")} `}{<a href={policyUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); if (!policyUrl) e.preventDefault(); }}>{translator("REGISTRATION_PAGE_FORM_POLICY_LINK")}</a>}.
			</span>
		</div>

		<div className="qplayer-register-form__actions">
			<ControlButton
				className={joinClassNames('qplayer-register-form__submit-button', configParams.isBonnierDi ? "qplayer-bold-font" : "")}
				style={{ backgroundColor: primaryColor, color: settings?.buttonTextColor || '#ffffff' }}
				hoverStyle={hoverStyle}
				onClick={onSubmit}
				disabled={!isPolicyAgreed}
			>
				{translator('LABEL_SUBMIT')}
			</ControlButton>
		</div>
	</div>;
};

export default DynamicForm;